import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "SpecialtyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Woven Wire Mesh",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Specialty Woven Wire Mesh",
      lead: "Engineered weave geometries and non-standard wire cloth constructions developed for demanding mechanical loads, accelerated filter cake discharge, and rigorous chemical processing environments.",
      secondaryCta: {
        label: "Woven Wire Mesh Overview",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "Specialty precision stainless steel woven wire mesh",
      },
      cardNote: "Engineered Weave Architectures",
      cardLink: {
        label: "Contact Engineering",
        to: "/ContactPage",
      },
      specs: [
        { label: "Filtration Span", value: "1 µm – 25 mm" },
        { label: "Mesh Count", value: "1 – 635 Mesh" },
        { label: "Wire Diameter", value: "0.020 – 12.0 mm" },
        { label: "Standard Alloys", value: "304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Specialized Engineering",
      heading: "Advanced Weave Architectures for Critical Industrial Processes",
      paragraphs: [
        "While standard square weaves meet broad screening requirements, challenging operating conditions often require specialized wire interlacing geometry. Our specialty woven mesh program encompasses asymmetric five-heddle satin weaves, heavy-gauge pre-crimped screens, and application-tailored custom weave combinations.",
        "Each architecture is selected or designed to address specific physical mechanisms, including minimizing particle entrapment, extending fatigue resistance under high differential pressures, or optimizing flow cross-sections through non-standard warp and shute wire configurations.",
        "Manufactured on precision industrial looms, these technical meshes are supplied in master roll widths up to 3,000 mm, cut-to-length panels, slit rolls, or processed into welded and framed filtration components ready for system integration.",
      ],
      keyPoints: [
        "Asymmetric five-heddle satin weaves providing one smooth low-friction working face for rapid cake release",
        "Heavy-duty pre-crimped wire cloth providing high aperture stability under abrasive screening loads",
        "Custom alloy and dimensional configurations aligned to ASTM E2016 and ISO 9044 manufacturing tolerances",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "Specialty precision stainless steel woven wire mesh rolls and edge construction",
      },
    },
    specTable: {
      heading: "Specialty Weave Classification Overview",
      intro: "Comparative parameters across our primary specialty woven mesh structural architectures.",
      columns: [
        { key: "category", label: "Weave Architecture", align: "left" },
        { key: "meshRange", label: "Mesh Count", align: "right" },
        { key: "wireRange", label: "Wire Diameter Range", align: "right" },
        { key: "surfaceType", label: "Surface Characteristics", align: "left" },
        { key: "primaryUse", label: "Primary Application Focus", align: "left" },
      ],
      rows: [
        {
          category: "Five-Heddle Weave",
          meshRange: "10 – 325 Mesh",
          wireRange: "0.035 – 0.45 mm",
          surfaceType: "Smooth one side / ribbed reverse",
          primaryUse: "Pressure leaf filters, cake discharge, underdrain",
        },
        {
          category: "Heavy-Duty Woven Mesh",
          meshRange: "Coarse / Open Sizing",
          wireRange: "1.20 – 12.0 mm",
          surfaceType: "Lock-crimp or flat-top rigidity",
          primaryUse: "Vibratory sizing, mineral grading, aggregate screening",
        },
        {
          category: "Custom Engineered Weave",
          meshRange: "1 – 635 Mesh",
          wireRange: "0.020 – 2.0 mm",
          surfaceType: "Tailored warp/shute ratio",
          primaryUse: "Polymer extrusion, chemical processing, OEM assemblies",
        },
      ],
      caption: "Values represent standard manufacturing capabilities. Specific wire diameter and aperture combinations are verified per order specification.",
    },
    features: {
      heading: "Structural & Manufacturing Capabilities",
      intro: "Core technical capabilities delivered across our specialty woven wire mesh lines.",
      items: [
        {
          title: "Engineered Interlacing",
          text: "Configured satin or heavy-duty weaves optimize flow geometry, surface smoothness, and mechanical stability across high-strain filtration cycles.",
        },
        {
          title: "Corrosion-Resistant Alloys",
          text: "Fabricated from verified austenitic stainless steels including AISI 304, 316, and low-carbon 316L, suitable for demanding chemical and thermal media.",
        },
        {
          title: "Dimensional Uniformity",
          text: "Weaving tension control ensures reproducible apertures and consistent physical spacing across master roll widths up to 3,000 mm.",
        },
        {
          title: "Comprehensive Converting",
          text: "Available as slit coils, stamped discs, pleated segments, and welded cylinders ready for seamless installation in filtration housings.",
        },
      ],
    },
    related: {
      heading: "Specialty Weave Variants",
      intro: "Explore our dedicated architectures designed for distinct filtration, screening, and OEM requirements.",
      cards: [
        {
          title: "Five-Heddle Weave",
          text: "5-harness satin weave presenting a smooth, planar contact surface on one side and drainage channels on the reverse for rapid cake discharge.",
          to: "/FiveHeddleWeavePage",
          badge: "Low-Friction Surface",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Five-heddle stainless steel woven wire mesh structure",
          },
        },
        {
          title: "Heavy-Duty Woven Mesh",
          text: "Heavy-gauge stainless steel screening with pre-crimped lock-crimp or flat-top structures engineered for severe abrasive impact.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "High Mechanical Rigidity",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Industrial heavy-duty pre-crimped stainless steel wire mesh",
          },
        },
        {
          title: "Custom Woven Mesh",
          text: "Bespoke warp-to-shute ratios, tailored aperture shapes, and specialty wire selections configured directly to equipment engineering specifications.",
          to: "/CustomWovenMeshPage",
          badge: "Engineered Solutions",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Precision custom engineered stainless steel woven wire mesh",
          },
        },
      ],
    },
    cta: {
      id: "specialty-cta",
      eyebrow: "Engineering Consultation",
      heading: "Request Specifications for Specialty Woven Wire Mesh",
      lead: "Our technical team reviews your operating media, mechanical load parameters, and dimensional criteria to recommend the ideal specialty weave structure.",
      secondaryLabel: "Explore Woven Wire Mesh",
      secondaryTo: "/WovenWireMeshPage",
      note: "Standard and custom weave specifications available upon request.",
    },
  },

  zh: {
    breadcrumbKey: "SpecialtyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "编织丝网",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "特种编织丝网",
      lead: "专为严苛机械负荷、快速滤饼剥离及复杂化学工艺介质开发的工程编织结构与非标特种编织丝网。",
      secondaryCta: {
        label: "编织丝网总览",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "特种精密不锈钢编织丝网",
      },
      cardNote: "工程编织结构",
      cardLink: {
        label: "技术咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "过滤跨度", value: "1 µm – 25 mm" },
        { label: "目数范围", value: "1 – 635 目" },
        { label: "丝径范围", value: "0.020 – 12.0 mm" },
        { label: "标准材质", value: "304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "特种织造工程",
      heading: "针对关键工业流程的高性能工程织造架构",
      paragraphs: [
        "常规方孔网能够满足广泛的常规筛分需求，但在严苛工况下，往往需要针对特定流体动力学与机械应力优化编织几何形式。我们的特种编织丝网系列涵盖不对称五综斜缎纹网、粗丝预弯重型轧花筛网，以及针对特定流阻要求定制的编织网。",
        "每种编织结构均针对特定物理机制开发：例如降低滤饼嵌塞阻力、提升高压差循环下的抗疲劳寿命，或通过非对称经纬配比实现更优的通流截面。",
        "产品由精密工业织网机严密织造，可提供最大幅宽达 3,000 mm 的母卷、定长裁剪片、高精度分条卷带，或深度加工为焊接与包边的成品过滤组件。",
      ],
      keyPoints: [
        "不对称五综斜缎纹提供单面平整光滑的工作面，利于滤饼快速脱落与清洗",
        "重型预弯轧花织造结构在重载冲击与磨损工况下保持孔径稳定不走样",
        "严格符合 ASTM E2016 及 ISO 9044 工业级尺寸公差与机械要求",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "特种精密不锈钢编织丝网卷料及边缘织造细节",
      },
    },
    specTable: {
      heading: "特种编织结构分类概述",
      intro: "核心特种编织丝网结构的几何特点与主要应用对照。",
      columns: [
        { key: "category", label: "编织形式", align: "left" },
        { key: "meshRange", label: "目数范围", align: "right" },
        { key: "wireRange", label: "丝径范围", align: "right" },
        { key: "surfaceType", label: "表面特征", align: "left" },
        { key: "primaryUse", label: "主要应用方向", align: "left" },
      ],
      rows: [
        {
          category: "五综编织网 (Five-Heddle)",
          meshRange: "10 – 325 目",
          wireRange: "0.035 – 0.45 mm",
          surfaceType: "单面平滑 / 背面导流棱",
          primaryUse: "叶滤机、滤饼快速脱落、底部支撑排液",
        },
        {
          category: "重型编织网 (Heavy-Duty)",
          meshRange: "粗孔 / 粗筛规格",
          wireRange: "1.20 – 12.0 mm",
          surfaceType: "锁弯或平顶高刚度结构",
          primaryUse: "振动筛分、矿石分级、骨料重载筛分",
        },
        {
          category: "定制工程编织网",
          meshRange: "1 – 635 目",
          wireRange: "0.020 – 2.0 mm",
          surfaceType: "定制经纬配比与孔径",
          primaryUse: "聚合物挤出过滤、化工提纯、精密 OEM 总成",
        },
      ],
      caption: "所列数据为常规生产能力范围。具体丝径、孔径与开孔率配比可根据订单技术要求核验提供。",
    },
    features: {
      heading: "结构与工艺制造能力",
      intro: "我们在特种不锈钢编织丝网领域提供的核心工程制造保障。",
      items: [
        {
          title: "专业结构织造",
          text: "通过五综、轧花及非对称编织形态优化流体通道与机械承载性能，应对周期性高压差工况。",
        },
        {
          title: "严选耐蚀合金",
          text: "常备 AISI 304、316 及低碳 316L 奥氏体不锈钢材料，化学成分稳定，耐温耐腐性能可靠。",
        },
        {
          title: "精密张力与公差",
          text: "数控织网张力控制系统确保幅宽范围内经纬排列平直均一，开孔公差稳定可靠。",
        },
        {
          title: "全流程后道加工",
          text: "支持分条、冲压成形、多层点焊、滚筒卷圆及金属包边，直接配套客户过滤系统。",
        },
      ],
    },
    related: {
      heading: "特种编织结构子类",
      intro: "浏览针对不同过滤截留、机械筛分及定制工况细分的产品系列。",
      cards: [
        {
          title: "五综编织丝网",
          text: "单面平整光滑低摩擦，背面具备坚固支撑骨架，是叶滤机与滤饼快速排放过滤的理想介质。",
          to: "/FiveHeddleWeavePage",
          badge: "低阻光滑表面",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "五综不锈钢编织丝网微观结构",
          },
        },
        {
          title: "重型编织网",
          text: "采用粗线径预弯成型定型结构，抗冲击耐磨损，保障矿山与骨料工业中大负荷物料筛分稳定。",
          to: "/HeavyDutyWovenMeshPage",
          badge: "高机械刚度",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "工业重型不锈钢预弯轧花编织丝网",
          },
        },
        {
          title: "定制编织网",
          text: "针对特殊孔径几何公差、经纬丝径配比及特种工况需求量身设计织造的工程丝网方案。",
          to: "/CustomWovenMeshPage",
          badge: "专属定制工程",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "高精度定制工程不锈钢编织丝网",
          },
        },
      ],
    },
    cta: {
      id: "specialty-cta",
      eyebrow: "技术选型支持",
      heading: "获取特种编织丝网技术规格与选型方案",
      lead: "我们的技术工程团队可根据您的介质特性、机械承压需求及安装尺寸，提供针对性的特种织造结构建议。",
      secondaryLabel: "返回编织丝网总览",
      secondaryTo: "/WovenWireMeshPage",
      note: "支持按订单要求提供图纸核对与材质报告。",
    },
  },

  es: {
    breadcrumbKey: "SpecialtyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Malla de alambre tejida",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Malla de alambre tejida especial",
      lead: "Geometrías de tejido avanzadas y configuraciones especiales diseñadas para altas cargas mecánicas, descarga acelerada de torta y procesos químicos exigentes.",
      secondaryCta: {
        label: "Malla tejida general",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "Malla de alambre tejida especial de acero inoxidable",
      },
      cardNote: "Estructuras de tejido técnico",
      cardLink: {
        label: "Contactar a ingeniería",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rango de filtración", value: "1 µm – 25 mm" },
        { label: "Conteo de malla", value: "1 – 635 Mesh" },
        { label: "Diámetro de alambre", value: "0.020 – 12.0 mm" },
        { label: "Aleaciones estándar", value: "304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería de tejido especializado",
      heading: "Arquitecturas de tejido avanzadas para procesos industriales críticos",
      paragraphs: [
        "Aunque el tejido cuadrado convencional cubre amplias necesidades de separación, las condiciones de proceso severas a menudo exigen geometrías de alambre especializadas. Nuestro programa de mallas tejidas especiales incluye tejido de cinco lizos de raso, cribas preonduladas de alambre grueso y especificaciones personalizadas.",
        "Cada estructura se selecciona para resolver desafíos mecánicos concretos: minimizar la colmatación de sólidos, resistir esfuerzos de fatiga bajo presiones diferenciales repetidas y asegurar un drenaje fluido mediante relaciones calculadas entre hilos de urdimbre y trama.",
        "Tejidas en telares industriales de alta precisión, estas telas metálicas se suministran en bobinas de hasta 3.000 mm de ancho, paneles cortados a medida, cintas refendidas o componentes filtrantes acabados con marcos y soldaduras.",
      ],
      keyPoints: [
        "Tejido de cinco lizos que proporciona una cara de trabajo lisa de baja fricción para desprendimiento rápido de torta",
        "Mallas preonduladas de servicio pesado con alta rigidez estructural para cribado abrasivo",
        "Conformidad con las tolerancias y requisitos de fabricación de ASTM E2016 e ISO 9044",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "Rollos de malla de alambre tejida especial de acero inoxidable",
      },
    },
    specTable: {
      heading: "Clasificación de mallas tejidas especiales",
      intro: "Comparativa técnica de las principales arquitecturas de tejido metálico especial.",
      columns: [
        { key: "category", label: "Tipo de tejido", align: "left" },
        { key: "meshRange", label: "Conteo de malla", align: "right" },
        { key: "wireRange", label: "Diámetro de alambre", align: "right" },
        { key: "surfaceType", label: "Características superficiales", align: "left" },
        { key: "primaryUse", label: "Aplicación principal", align: "left" },
      ],
      rows: [
        {
          category: "Tejido de cinco lizos (Five-Heddle)",
          meshRange: "10 – 325 Mesh",
          wireRange: "0.035 – 0.45 mm",
          surfaceType: "Cara lisa / reverso con canales",
          primaryUse: "Filtros de hojas a presión, descarga de torta, soporte",
        },
        {
          category: "Malla tejida de servicio pesado",
          meshRange: "Aperturas gruesas",
          wireRange: "1.20 – 12.0 mm",
          surfaceType: "Estructura preondulada trabada o plana",
          primaryUse: "Clasificación vibratoria, tamizado de áridos y minerales",
        },
        {
          category: "Malla tejida a medida",
          meshRange: "1 – 635 Mesh",
          wireRange: "0.020 – 2.0 mm",
          surfaceType: "Relación personalizada urdimbre/trama",
          primaryUse: "Extrusión de polímeros, industria química, conjuntos OEM",
        },
      ],
      caption: "Los valores indican rangos habituales de fabricación. Las combinaciones exactas se confirman bajo especificación de pedido.",
    },
    features: {
      heading: "Capacidades técnicas y de fabricación",
      intro: "Ventajas de fabricación incorporadas en nuestras mallas de alambre especiales.",
      items: [
        {
          title: "Tejido estructural técnico",
          text: "Estructuras raso o preonduladas que optimizan la dinámica de flujo y la estabilidad mecánica bajo ciclos de presión continuos.",
        },
        {
          title: "Aleaciones resistentes a corrosión",
          text: "Fabricación en aceros inoxidables austeníticos certificados AISI 304, 316 y 316L para fluidos químicos y térmicos exigentes.",
        },
        {
          title: "Uniformidad dimensional",
          text: "Control riguroso de tensión en telar para garantizar aberturas regulares en anchos de rollo de hasta 3.000 mm.",
        },
        {
          title: "Transformación y conformado",
          text: "Capacidad de corte en discos, tiras longitudinales, plisados y cilindros soldados listos para su montaje directo.",
        },
      ],
    },
    related: {
      heading: "Variantes de tejido especial",
      intro: "Conozca en detalle las tipologías desarrolladas para filtración, tamizado y requisitos OEM.",
      cards: [
        {
          title: "Tejido de cinco lizos",
          text: "Superficie de trabajo completamente plana de baja fricción combinada con nervaduras de soporte para rápida evacuación de sólidos.",
          to: "/FiveHeddleWeavePage",
          badge: "Superficie de baja fricción",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Estructura de tejido de cinco lizos en acero inoxidable",
          },
        },
        {
          title: "Malla tejida de servicio pesado",
          text: "Malla de alambre grueso preondulado de alta rigidez para soportar impactos abrasivos y cargas pesadas de cribado.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Alta rigidez mecánica",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Malla de alambre tejida preondulada para servicio pesado",
          },
        },
        {
          title: "Malla tejida a medida",
          text: "Diseño personalizado de aperturas, diámetros de hilo y proporciones de tejido adaptados a las tolerancias de su equipo.",
          to: "/CustomWovenMeshPage",
          badge: "Soluciones a medida",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Malla de alambre de precisión tejida a medida",
          },
        },
      ],
    },
    cta: {
      id: "specialty-cta",
      eyebrow: "Consulta técnica",
      heading: "Solicitar especificaciones para malla tejida especial",
      lead: "Nuestro equipo de ingeniería analiza sus parámetros de flujo, cargas mecánicas y dimensiones para sugerir la estructura de tejido óptima.",
      secondaryLabel: "Ver malla de alambre tejida",
      secondaryTo: "/WovenWireMeshPage",
      note: "Disponibilidad de especificaciones estándar y personalizadas bajo consulta.",
    },
  },

  de: {
    breadcrumbKey: "SpecialtyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Drahtgewebe",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Spezial-Drahtgewebe",
      lead: "Konstruierte Gewebestrukturen und Sonderbindungen für extreme mechanische Beanspruchung, beschleunigten Filterkuchenabwurf und fordernde chemische Prozesse.",
      secondaryCta: {
        label: "Drahtgewebe Übersicht",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "Präzisions-Spezialdrahtgewebe aus Edelstahl",
      },
      cardNote: "Konstruierte Webarchitekturen",
      cardLink: {
        label: "Technischer Kontakt",
        to: "/ContactPage",
      },
      specs: [
        { label: "Filtrationsbereich", value: "1 µm – 25 mm" },
        { label: "Maschenbereich", value: "1 – 635 Mesh" },
        { label: "Drahtdurchmesser", value: "0,020 – 12,0 mm" },
        { label: "Standardlegierungen", value: "304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Spezialisierte Webtechnik",
      heading: "Fortschrittliche Webarchitekturen für anspruchsvolle Trennverfahren",
      paragraphs: [
        "Während Standard-Quadratmaschen breite Siebanforderungen abdecken, erfordern herausfordernde Betriebsbedingungen oft optimierte Drahtgeometrien. Unser Spezialgewebeprogramm umfasst asymmetrische 5-Schaft-Atlasbindungen, dickdrähtige vorgekröpfte Siebe sowie applikationsspezifische Sonderkonstruktionen.",
        "Jede Bindungsform ist auf konkrete physikalische Anforderungen ausgelegt: Minimierung des Kuchenwiderstands, hohe Ermüdungsfestigkeit bei Differenzdruckwechseln oder optimierte Durchströmung durch spezifische Verhältnisse von Kette und Schuss.",
        "Auf modernen Industrie-Webmaschinen gefertigt, liefern wir Spezialgewebe in Rollenbreiten bis zu 3.000 mm, Fixzuschnitten, Spaltbändern oder fertig konfektionierten Filterelementen.",
      ],
      keyPoints: [
        "Asymmetrische 5-Schaft-Atlasbindung mit glatter, reibungsarmer Oberseite für schnellen Kuchenabwurf",
        "Vorgekröpfte Schwerlastgewebe für hohe Formstabilität unter abrasiver Schüttgutbeanspruchung",
        "Gefertigt nach Fertigungstoleranzen gemäß ASTM E2016 und ISO 9044",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "Edelstahl-Spezialdrahtgewebe Rollen und Randverarbeitung",
      },
    },
    specTable: {
      heading: "Klassifizierung der Spezialdrahtgewebe",
      intro: "Vergleichende Übersicht der wesentlichen Bindungsarten unseres Spezialprogramms.",
      columns: [
        { key: "category", label: "Gewebebindung", align: "left" },
        { key: "meshRange", label: "Maschenbereich", align: "right" },
        { key: "wireRange", label: "Drahtdurchmesser", align: "right" },
        { key: "surfaceType", label: "Oberflächenbeschaffenheit", align: "left" },
        { key: "primaryUse", label: "Haupteinsatzbereich", align: "left" },
      ],
      rows: [
        {
          category: "Fünfschaftgewebe (Five-Heddle)",
          meshRange: "10 – 325 Mesh",
          wireRange: "0,035 – 0,45 mm",
          surfaceType: "Glatt einseitig / gerippte Rückseite",
          primaryUse: "Druckblattfilter, Kuchenabwurf, Drainageschichten",
        },
        {
          category: "Schwerlast-Drahtgewebe",
          meshRange: "Grobsiebung",
          wireRange: "1,20 – 12,0 mm",
          surfaceType: "Schlosskröpfung oder flache Oberfläche",
          primaryUse: "Vibrationssiebung, Mineralienaufbereitung, Grobabsiebung",
        },
        {
          category: "Kundenspezifisches Gewebe",
          meshRange: "1 – 635 Mesh",
          wireRange: "0,020 – 2,0 mm",
          surfaceType: "Individuelle Ketten-/Schussabstimmung",
          primaryUse: "Polymer-Extrusion, chemische Industrie, OEM-Module",
        },
      ],
      caption: "Angaben stellen Richtwerte dar. Exakte Kombinationen aus Drahtstärke und Öffnungsweite werden auftragsspezifisch abgestimmt.",
    },
    features: {
      heading: "Konstruktions- & Fertigungskompetenz",
      intro: "Technische Eigenschaften unserer industriellen Spezialgewebe.",
      items: [
        {
          title: "Spezifische Bindungsgeometrie",
          text: "Atlas- oder Kröpfungsstrukturen sichern strömungsgünstigen Durchfluss und hohe Dauerstandfestigkeit bei Druckzyklen.",
        },
        {
          title: "Korrosionsfeste Legierungen",
          text: "Verarbeitung geprüfter austenitischer Edelstähle wie AISI 304, 316 und kohlenstoffarmer 316L für anspruchsvolle Medien.",
        },
        {
          title: "Maßhaltigkeit und Planlage",
          text: "Kontrollierte Webspannung garantiert gleichmäßige Maschenweiten über Rollenbreiten von bis zu 3.000 mm.",
        },
        {
          title: "Weiterverarbeitung",
          text: "Konfektionierung zu Ronden, Schmalbändern, Zylindern und eingefassten Filterplatten nach Kundenzeichnung.",
        },
      ],
    },
    related: {
      heading: "Spezialgewebe-Varianten",
      intro: "Erfahren Sie mehr über unsere spezialisierten Webarten für individuelle Filtrationsanforderungen.",
      cards: [
        {
          title: "Fünfschaftgewebe (Five-Heddle)",
          text: "Besonders glatte Oberseite für minimale Kuchenanhaftung, kombiniert mit rückseitiger Drainagestruktur.",
          to: "/FiveHeddleWeavePage",
          badge: "Reibungsarme Oberfläche",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Struktur von Fünfschaft-Drahtgewebe aus Edelstahl",
          },
        },
        {
          title: "Schwerlast-Drahtgewebe",
          text: "Dickdrähtige Schlosskröpfung für höchste mechanische Belastung und Widerstandskraft im Schüttgutbereich.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Hohe mechanische Stabilität",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Vorgekröpftes Schwerlast-Drahtgewebe aus Edelstahl",
          },
        },
        {
          title: "Kundenspezifisches Gewebe",
          text: "Maßgeschneiderte Maschen- und Drahtkonfigurationen für individuelle Baugruppen und anspruchsvolle OEM-Systeme.",
          to: "/CustomWovenMeshPage",
          badge: "Individuelle Lösungen",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Kundenspezifisch gewebtes Edelstahl-Drahtgewebe",
          },
        },
      ],
    },
    cta: {
      id: "specialty-cta",
      eyebrow: "Technische Beratung",
      heading: "Spezifikationen für Spezial-Drahtgewebe anfragen",
      lead: "Unsere Ingenieure prüfen Ihre Prozessdaten, mechanischen Einbaubedingungen und Toleranzen für die passende Gewebelösung.",
      secondaryLabel: "Drahtgewebe entdecken",
      secondaryTo: "/WovenWireMeshPage",
      note: "Standard- und Sonderspezifikationen auf Anfrage lieferbar.",
    },
  },

  fr: {
    breadcrumbKey: "SpecialtyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Toile métallique tissée",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Toiles métalliques tissées spéciales",
      lead: "Géométries de tissage avancées et toiles spéciales conçues pour contraintes mécaniques sévères, décolmatage rapide du gâteau et milieux chimiques agressifs.",
      secondaryCta: {
        label: "Aperçu des toiles tissées",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "Toile tissée inox de haute précision pour filtration spéciale",
      },
      cardNote: "Architectures de tissage technique",
      cardLink: {
        label: "Contacter l'ingénierie",
        to: "/ContactPage",
      },
      specs: [
        { label: "Plage de filtration", value: "1 µm – 25 mm" },
        { label: "Nombre de mailles", value: "1 – 635 Mesh" },
        { label: "Diamètre de fil", value: "0,020 – 12,0 mm" },
        { label: "Alliages standards", value: "304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Tissage technique spécialisé",
      heading: "Structures tissées avancées pour procédés industriels critiques",
      paragraphs: [
        "Si les armures unies à mailles carrées répondent aux criblages courants, les conditions d'exploitation sévères requièrent des architectures de tissage spécifiques. Notre gamme spéciale comprend le tissage 5 cadres (armure satin), les toiles pré-ondulées en gros fils et les fabrications sur mesure.",
        "Chaque structure est développée pour répondre à des contraintes physiques précises : réduction de l'adhérence du gâteau de filtration, longévité accrue sous cycles de pression différentielle et optimisation de l'écoulement par des rapports adaptés de fils de chaîne et de trame.",
        "Tissées sur des métiers industriels de haute précision, nos toiles sont proposées en rouleaux jusqu'à 3 000 mm de largeur, panneaux découpés, bandes refendues ou sous forme d'éléments filtrants assemblés prêts à l'intégration.",
      ],
      keyPoints: [
        "Tissage satin 5 cadres offrant une face de travail lisse et plane pour un décolmatage rapide du gâteau",
        "Toiles métalliques renforcées pré-ondulées maintenant une stabilité d'ouverture sous fortes vibrations",
        "Conformité avec les tolérances de tissage des normes ASTM E2016 et ISO 9044",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "Rouleaux de toile métallique spéciale en acier inoxydable",
      },
    },
    specTable: {
      heading: "Classification des toiles métalliques spéciales",
      intro: "Caractéristiques comparatives de nos principales structures de tissage spécial.",
      columns: [
        { key: "category", label: "Armure de tissage", align: "left" },
        { key: "meshRange", label: "Nombre de mailles", align: "right" },
        { key: "wireRange", label: "Diamètre de fil", align: "right" },
        { key: "surfaceType", label: "État de surface", align: "left" },
        { key: "primaryUse", label: "Application principale", align: "left" },
      ],
      rows: [
        {
          category: "Tissage 5 cadres (Five-Heddle)",
          meshRange: "10 – 325 Mesh",
          wireRange: "0,035 – 0,45 mm",
          surfaceType: "Face lisse / dos rainuré de drainage",
          primaryUse: "Filtres à plaques sous pression, décolmatage, drainage",
        },
        {
          category: "Toile tissée renforcée (Heavy-Duty)",
          meshRange: "Grosses ouvertures",
          wireRange: "1,20 – 12,0 mm",
          surfaceType: "Pré-ondulée crantée ou à surface plane",
          primaryUse: "Criblage vibrant, calibrage minier, granulats",
        },
        {
          category: "Toile tissée sur mesure",
          meshRange: "1 – 635 Mesh",
          wireRange: "0,020 – 2,0 mm",
          surfaceType: "Rapport chaîne/trame personnalisé",
          primaryUse: "Extrusion de polymères, chimie, sous-ensembles OEM",
        },
      ],
      caption: "Les données correspondent aux plages courantes. Les combinaisons spécifiques sont validées selon le cahier des charges.",
    },
    features: {
      heading: "Capacités techniques et de fabrication",
      intro: "Points forts appliqués à notre offre de toiles métalliques spéciales.",
      items: [
        {
          title: "Tissage structurel optimisé",
          text: "Formes satinées ou ondulées assurant une circulation fluide et une résistance mécanique prolongée sous contraintes alternées.",
        },
        {
          title: "Alliages résistants à la corrosion",
          text: "Réalisation en aciers inoxydables AISI 304, 316 et 316L bas carbone pour environnements thermiques et chimiques rigoureux.",
        },
        {
          title: "Précision dimensionnelle",
          text: "Tension rigoureusement régulée garantissant la régularité des mailles sur des largeurs atteignant 3 000 mm.",
        },
        {
          title: "Opérations de parachèvement",
          text: "Façonnage en disques, bandes refendues, cylindres roulés soudés et cadres métalliques étanches.",
        },
      ],
    },
    related: {
      heading: "Variantes de tissage spécial",
      intro: "Découvrez nos gammes dédiées selon votre procédé de filtration ou de séparation mécanique.",
      cards: [
        {
          title: "Tissage 5 cadres (Five-Heddle)",
          text: "Face unilatérale lisse à faible frottement et nervures de renfort au dos, idéal pour filtres à gâteau.",
          to: "/FiveHeddleWeavePage",
          badge: "Surface lisse anti-adhérente",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Schéma structurel de toile métallique en inox à cinq lames",
          },
        },
        {
          title: "Toile tissée renforcée",
          text: "Structure en fils épais pré-ondulés offrant une haute rigidité face à l'abrasion et aux chocs industriels.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Haute rigidité mécanique",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Toile métallique tissée pré-ondulée pour service lourd",
          },
        },
        {
          title: "Toile tissée sur mesure",
          text: "Conception sur cahier des charges des ouvertures, des fils et des dimensions adaptées à vos carters.",
          to: "/CustomWovenMeshPage",
          badge: "Solutions sur mesure",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Structure de toile métallique sur mesure en acier inoxydable",
          },
        },
      ],
    },
    cta: {
      id: "specialty-cta",
      eyebrow: "Étude d'ingénierie",
      heading: "Demander une offre pour toiles métalliques spéciales",
      lead: "Nos ingénieurs examinent vos paramètres de fluide, contraintes mécaniques et encombrements pour sélectionner la toile adéquate.",
      secondaryLabel: "Découvrir les toiles tissées",
      secondaryTo: "/WovenWireMeshPage",
      note: "Spécifications standards et sur mesure livrables selon commande.",
    },
  },

  pt: {
    breadcrumbKey: "SpecialtyWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Telas metálicas tecidas",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Telas metálicas tecidas especiais",
      lead: "Arquiteturas avançadas de tecelagem e telas especiais projetadas para elevadas cargas mecânicas, rápida liberação de sólidos e processos químicos severos.",
      secondaryCta: {
        label: "Visão geral de telas tecidas",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "Tela tecida de aço inoxidável de precisão especial",
      },
      cardNote: "Estruturas técnicas de tecelagem",
      cardLink: {
        label: "Contato técnico",
        to: "/ContactPage",
      },
      specs: [
        { label: "Faixa de filtração", value: "1 µm – 25 mm" },
        { label: "Contagem de malha", value: "1 – 635 Mesh" },
        { label: "Diâmetro do arame", value: "0,020 – 12,0 mm" },
        { label: "Ligas padrão", value: "304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Engenharia de tecelagem especializada",
      heading: "Arquiteturas tecidas avançadas para processos industriais críticos",
      paragraphs: [
        "Embora as malhas quadradas convencionais atendam a uma ampla gama de aplicações, exigências severas frequentemente requerem geometrias de arame diferenciadas. Nossa linha de telas especiais inclui tecelagem de cinco liços em cetim, telas pré-onduladas de fios grossos e especificações sob medida.",
        "Cada padrão é desenvolvido para atender requisitos físicos específicos: evitar o entupimento por torta de sólidos, resistir à fadiga mecânica sob ciclos de pressão diferencial e garantir drenagem eficiente através de proporções calculadas entre urdume e trama.",
        "Produzidas em teares industriais de precisão, as telas são fornecidas em rolos de até 3.000 mm de largura, painéis cortados, fitas cortadas longitudinalmente ou conjuntos filtrantes montados com solda e bordas.",
      ],
      keyPoints: [
        "Tecelagem de 5 liços com superfície plana de baixo atrito para desprendimento rápido da torta de filtro",
        "Telas pré-onduladas para serviço pesado com alta rigidez estrutural sob peneiramento abrasivo",
        "Conformidade rigorosa com tolerâncias dimensionais das normas ASTM E2016 e ISO 9044",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
        alt: "Rolos de tela metálica tecida especial de aço inoxidável",
      },
    },
    specTable: {
      heading: "Classificação de telas tecidas especiais",
      intro: "Comparativo técnico das principais geometrias de telas metálicas especiais.",
      columns: [
        { key: "category", label: "Tipo de trama", align: "left" },
        { key: "meshRange", label: "Contagem de malha", align: "right" },
        { key: "wireRange", label: "Diâmetro do arame", align: "right" },
        { key: "surfaceType", label: "Características da superfície", align: "left" },
        { key: "primaryUse", label: "Aplicação principal", align: "left" },
      ],
      rows: [
        {
          category: "Tecelagem de 5 liços (Five-Heddle)",
          meshRange: "10 – 325 Mesh",
          wireRange: "0,035 – 0,45 mm",
          surfaceType: "Face lisa / reverso com nervuras",
          primaryUse: "Filtros de folhas sob pressão, descarga de torta, drenagem",
        },
        {
          category: "Tela tecida de alta resistência",
          meshRange: "Aberturas grossas",
          wireRange: "1,20 – 12,0 mm",
          surfaceType: "Estrutura pré-ondulada travada ou plana",
          primaryUse: "Peneiramento vibratório, mineração, classificação de britas",
        },
        {
          category: "Tela tecida sob medida",
          meshRange: "1 – 635 Mesh",
          wireRange: "0,020 – 2,0 mm",
          surfaceType: "Proporção personalizada urdume/trama",
          primaryUse: "Extrusão de polímeros, petroquímica, conjuntos OEM",
        },
      ],
      caption: "Os valores refletem faixas usuais de fabricação. Configurações sob medida são confirmadas conforme especificação do pedido.",
    },
    features: {
      heading: "Capacidades técnicas e de fabricação",
      intro: "Garantias estruturais incorporadas em nossa linha de telas especiais.",
      items: [
        {
          title: "Trama técnica estruturada",
          text: "Padrões em cetim ou pré-ondulados otimizam os canais de fluxo e a resistência mecânica sob ciclos de pressão repetidos.",
        },
        {
          title: "Ligas resistentes à corrosão",
          text: "Fabricadas com aços inoxidáveis austeníticos AISI 304, 316 e 316L de baixo carbono para meios quimicamente agressivos.",
        },
        {
          title: "Estabilidade dimensional",
          text: "Controle uniforme de tensão nos teares garantindo aberturas homogêneas em rolos de até 3.000 mm de largura.",
        },
        {
          title: "Processamento e corte",
          text: "Fornecimento em discos estampados, bobinas fracionadas, cilindros soldados e telas emolduradas para fácil integração.",
        },
      ],
    },
    related: {
      heading: "Variantes de tecelagem especial",
      intro: "Conheça nossas tipologias estruturadas para diferentes condições de filtragem e classificação.",
      cards: [
        {
          title: "Tecelagem de 5 liços",
          text: "Superfície de trabalho plana de baixo atrito com canais de suporte no verso para rápida ejeção de sólidos.",
          to: "/FiveHeddleWeavePage",
          badge: "Superfície de baixo atrito",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Estrutura de tela tecida de cinco liços em aço inoxidável",
          },
        },
        {
          title: "Tela tecida de alta resistência",
          text: "Fios espessos pré-ondulados proporcionando máxima resistência mecânica contra desgaste abrasivo intenso.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Alta rigidez mecânica",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Tela metálica tecida pré-ondulada para serviço pesado",
          },
        },
        {
          title: "Tela tecida sob medida",
          text: "Engenharia dedicada na combinação de aberturas, diâmetros de arame e formatos de acordo com o projeto OEM.",
          to: "/CustomWovenMeshPage",
          badge: "Soluções sob medida",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Estrutura de tela tecida de aço inoxidável personalizada",
          },
        },
      ],
    },
    cta: {
      id: "specialty-cta",
      eyebrow: "Consultoria técnica",
      heading: "Solicitar especificações para telas tecidas especiais",
      lead: "Nossa equipe avalia suas condições operacionais, pressão de processo e requisitos dimensionais para indicar a trama ideal.",
      secondaryLabel: "Explorar telas tecidas",
      secondaryTo: "/WovenWireMeshPage",
      note: "Especificações padronizadas e personalizadas disponíveis sob consulta.",
    },
  },
};
